import MaterialTable from '@material-table/core';
import {
	AddCircleOutline,
	AdsClickOutlined,
	BlockOutlined,
	EditOffOutlined,
	EditOutlined,
	SettingsOutlined,
	VisibilityOffOutlined,
} from '@mui/icons-material';
import {
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material';
import { get, post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Formik } from 'formik';
import { enqueueSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import { ConfirmDialog } from 'src/components/confirmDialog/confirmDialog';
import { PageAppBar } from 'src/components/pageAppBar';
import { UserContext } from 'src/contexts/UserContext';
import { actionOneButtonStyle, actionTwoButtonStyle } from 'src/theme';
import { object, string } from 'yup';

/**
 * The Campaigns component is use to tie a segment with a List Management Profile
 * Expects No params our query string values on the route
 * * @category Pages
 *  @component
 *
 */
export function Users() {
	const userContext = useContext(UserContext);
	const [users, setUsers] = useState([]);
	const [editUser, setEditUser] = useState({});
	const [toggleStatusConfirm, setToggleStatusConfirm] = useState(false);
	const [userSettings, setUserSettings] = useState(false);
	const [newUser, setNewUser] = useState(false);
	const [editUserSettings, setEditUserSettings] = useState(false);

	useEffect(() => {
		if (userContext.tenantId) {
			getUsers();
			console.log('User context', userContext);
		}
	}, [userContext.tenantId]);

	async function getUsers() {
		try {
			const userResponse = await get({
				apiName: 'cdyxoutreach',
				path: '/admin/list-users',
				options: {
					headers: {
						Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
				},
			}).response;

			const userResults = await userResponse.body.json();

			console.log('User results', userResults);

			for (const user of userResults) {
				if (user.securityOptions) {
					const securityOptions = user.securityOptions.split('|');
					user.contacts = securityOptions[0];
					user.inventoryManagement = securityOptions[1];
					user.campaigns = securityOptions[2];
					user.communicationProfiles = securityOptions[3];
					user.digitalChannels = securityOptions[4];
					user.suppression = securityOptions[5];
					user.liveAgentSelect = securityOptions[6];
					user.connection = securityOptions[7];
					user.userManagement = securityOptions[8];
				} else {
					user.contacts = '0';
					user.inventoryManagement = '0';
					user.campaigns = '0';
					user.communicationProfiles = '0';
					user.digitalChannels = '0';
					user.suppression = '0';
					user.liveAgentSelect = '0';
					user.connection = '0';
					user.userManagement = '0';
				}
			}

			setUsers(userResults);
		} catch (err) {
			console.log('Error getting users', err);
		}
	}

	async function updateUserStatus() {
		try {
			setToggleStatusConfirm(false);
			let action = editUser.status === 'Enabled' ? 'disable' : 'enable';

			await post({
				apiName: 'cdyxoutreach',
				path: `/admin/edit-user/${action}`,
				options: {
					body: {
						user: editUser,
					},
					headers: {
						Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
						'x-api-key': userContext.apiKey,
						'Content-Type': 'application/json',
					},
				},
			}).response;

			getUsers();
			enqueueSnackbar(`${editUser.name}'s account has been ${action}d`, {
				variant: 'success',
			});
		} catch (err) {
			console.log('Error updating user status', err);
			enqueueSnackbar('Error updating user status', { variant: 'error' });
		}
	}

	return (
		<>
			<PageAppBar
				title="Users"
				description="Manage users and their permissions"
				actionOneText={
					userContext.securityOptions.userManagement > 1 ? '+ User' : ''
				}
				actionOneHandler={() => {
					setUserSettings(true);
					setEditUserSettings(true);
					setNewUser(true);
					setEditUser({
						name: '',
						email: '',
						company: '',
						approver: '0',
						contacts: '0',
						inventoryManagement: '0',
						campaigns: '0',
						communicationProfiles: '0',
						digitalChannels: '0',
						suppression: '0',
						liveAgentSelect: '0',
						connection: '0',
						userManagement: '0',
					});
				}}
			/>
			<Grid container direction="column">
				<Grid item>
					<MaterialTable
						components={{
							Container: (props) => <Box {...props} elevation={0} />,
						}}
						columns={[
							{ title: 'Name', field: 'name' },
							{ title: 'Email', field: 'email' },
							{ title: 'Company', field: 'company' },
							{ title: 'Status', field: 'status' },
						]}
						title=""
						data={users}
						options={{
							actionsColumnIndex: -1,
							pageSize: 10,
							searchText: window.sessionStorage.getItem('ListTenantsSearch'),
						}}
						isLoading={users.length === 0}
						actions={[
							{
								icon: () => <SettingsOutlined color="primary" />,
								tooltip: 'User Settings',
								onClick: (event, rowData) => {
									setUserSettings(true);
									setEditUser(rowData);
								},
							},
							(rowData) => ({
								icon: () =>
									rowData.status === 'Enabled' ? (
										<BlockOutlined
											color={
												rowData.id !== userContext.id ? 'error' : 'disabled'
											}
										/>
									) : (
										<AddCircleOutline color="primary" />
									),
								disabled:
									rowData.id === userContext.id ||
									userContext.securityOptions.userManagement < 2,
								tooltip:
									rowData.status === 'Enabled' ? 'Disable User' : 'Enable User',
								onClick: (event, rowData) => {
									setToggleStatusConfirm(true);
									setEditUser(rowData);
								},
							}),
						]}
					></MaterialTable>
				</Grid>

				<ConfirmDialog
					open={toggleStatusConfirm}
					title="Confirm User Status Change"
					description={`Are you sure you want to ${editUser.status === 'Enabled' ? 'disable' : 'enable'} ${editUser.name}'s account?`}
					actionTwoText="Cancel"
					actionTwoHandler={() => {
						setToggleStatusConfirm(false);
					}}
					actionOneText="Confirm"
					actionOneHandler={() => {
						updateUserStatus();
					}}
				/>

				<Dialog
					open={userSettings}
					maxWidth="xl"
					fullWidth
					onClose={() => {
						setUserSettings(false);
					}}
				>
					<DialogTitle>
						{!newUser ? 'User Settings' : 'Create User'}
					</DialogTitle>
					<DialogContent>
						{editUserSettings && (
							<Formik
								initialValues={editUser}
								onSubmit={async (values) => {
									console.log('Save user', values);
									if (values.id) {
										try {
											const formattedUser = {
												...values,
												securityOptions: `${values.contacts}|${values.inventoryManagement}|${values.campaigns}|${values.communicationProfiles}|${values.digitalChannels}|${values.suppression}|${values.liveAgentSelect}|${values.connection}|${values.userManagement}`,
											};
											delete formattedUser.contacts;
											delete formattedUser.inventoryManagement;
											delete formattedUser.campaigns;
											delete formattedUser.communicationProfiles;
											delete formattedUser.digitalChannels;
											delete formattedUser.suppression;
											delete formattedUser.liveAgentSelect;
											delete formattedUser.connection;
											delete formattedUser.userManagement;
											delete formattedUser.status;
											delete formattedUser.tableData;

											await post({
												apiName: 'cdyxoutreach',
												path: '/admin/edit-user',
												options: {
													body: {
														user: formattedUser,
													},
													headers: {
														Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
														'x-api-key': userContext.apiKey,
														'Content-Type': 'application/json',
													},
												},
											}).response;

											getUsers();
											setEditUserSettings(false);
											enqueueSnackbar(
												`${values.name}'s settings have been saved`,
												{ variant: 'success' },
											);
											setUserSettings(false);
										} catch (err) {
											console.log('Error saving user', err);
											enqueueSnackbar('Error saving user', {
												variant: 'error',
											});
										}
									} else {
										try {
											await post({
												apiName: 'cdyxoutreach',
												path: '/admin/user',
												options: {
													body: {
														email: values.email,
														name: values.name,
														tenant: userContext.tenantId,
														password: values.password,
														tenantRole: 'User',
														securityOptions: `${values.contacts}|${values.inventoryManagement}|${values.campaigns}|${values.communicationProfiles}|${values.digitalChannels}|${values.suppression}|${values.liveAgentSelect}|${values.connection}|${values.userManagement}`,
														company: values.company,
														approver: values.approver === '1' ? '1' : '0',
													},
													headers: {
														Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
														'x-api-key': userContext.apiKey,
														'Content-Type': 'application/json',
													},
												},
											}).response;

											getUsers();
											setNewUser(false);
											setEditUserSettings(false);
											setUserSettings(false);
											enqueueSnackbar(
												`User for ${values.name} has been created`,
												{ variant: 'success' },
											);
										} catch (err) {
											console.log('Error creating user', err);
											enqueueSnackbar('Error creating user', {
												variant: 'error',
											});
										}
									}
								}}
								enableReinitialize
								validationSchema={object().shape({
									name: string().required('Name is required'),
									email: string()
										.email('Invalid email')
										.required('Email is required'),
									company: string().required('Company is required'),
									approver: string().required('Approver is required'),
									contacts: string().required('Contacts is required'),
									inventoryManagement: string().required(
										'Inventory Management is required',
									),
									campaigns: string().required('Campaigns is required'),
									communicationProfiles: string().required(
										'Communication Profiles is required',
									),
									digitalChannels: string().required(
										'Digital Channels is required',
									),
									suppression: string().required('Suppression is required'),
									liveAgentSelect: string().required(
										'Live Agent Select is required',
									),
									connection: string().required('Connection is required'),
									userManagement: string().required(
										'User Management is required',
									),
									// Password is only required for new users
									password: newUser
										? string().required('Password is required')
										: string().notRequired(),
								})}
							>
								{(props) => {
									const {
										values,
										touched,
										errors,
										handleChange,
										handleBlur,
										handleSubmit,
										isSubmitting,
										setFieldValue,
									} = props;
									return (
										<form onSubmit={handleSubmit}>
											<Box display="flex" flexDirection="column" gap={2}>
												<Box
													display="grid"
													gridTemplateColumns={
														newUser ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr'
													}
													gap={2}
													sx={{ marginTop: 2 }}
												>
													<TextField
														id="name"
														label="Name"
														value={values.name}
														onChange={handleChange}
														onBlur={handleBlur}
														error={errors.name && touched.name}
														helperText={
															errors.name && touched.name && errors.name
														}
													/>
													<TextField
														id="email"
														label="Email"
														value={values.email}
														onChange={handleChange}
														onBlur={handleBlur}
														error={errors.email && touched.email}
														helperText={
															errors.email && touched.email && errors.email
														}
													/>
													<TextField
														id="company"
														label="Company"
														value={values.company}
														onChange={handleChange}
														onBlur={handleBlur}
														error={errors.company && touched.company}
														helperText={
															errors.company &&
															touched.company &&
															errors.company
														}
													/>
													{newUser && (
														<TextField
															id="password"
															label="Password"
															value={values.password}
															onChange={handleChange}
															onBlur={handleBlur}
															error={errors.password && touched.password}
															helperText={
																errors.password &&
																touched.password &&
																errors.password
															}
														/>
													)}
												</Box>
												<Card variant="outlined">
													<CardHeader
														title="Security Rights"
														titleTypographyProps={{ variant: 'body2' }}
													/>
													<CardContent>
														<Table>
															<TableHead>
																<TableRow>
																	<TableCell>Contacts</TableCell>
																	<TableCell>Inventory Management</TableCell>
																	<TableCell>Campaigns</TableCell>
																	<TableCell>Communication Profiles</TableCell>
																	<TableCell>Digital Channels</TableCell>
																</TableRow>
															</TableHead>
															<TableBody>
																<TableRow>
																	<TableCell>
																		<ToggleButtonGroup
																			value={values.contacts}
																			exclusive
																			onChange={(e, value) => {
																				setFieldValue('contacts', value);
																			}}
																		>
																			<Tooltip title="No Access">
																				<ToggleButton value="0">
																					<VisibilityOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Read Only">
																				<ToggleButton value="1">
																					<EditOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Read/Write">
																				<ToggleButton value="2">
																					<EditOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																		</ToggleButtonGroup>
																	</TableCell>
																	<TableCell>
																		<ToggleButtonGroup
																			value={values.inventoryManagement}
																			exclusive
																			onChange={(e, value) => {
																				setFieldValue(
																					'inventoryManagement',
																					value,
																				);
																			}}
																		>
																			<Tooltip title="No Access">
																				<ToggleButton value="0">
																					<VisibilityOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Read Only">
																				<ToggleButton value="1">
																					<EditOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Read/Write">
																				<ToggleButton value="2">
																					<EditOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																		</ToggleButtonGroup>
																	</TableCell>
																	<TableCell>
																		<ToggleButtonGroup
																			value={values.campaigns}
																			exclusive
																			onChange={(e, value) => {
																				setFieldValue('campaigns', value);
																			}}
																		>
																			<Tooltip title="No Access">
																				<ToggleButton value="0">
																					<VisibilityOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Read Only">
																				<ToggleButton value="1">
																					<EditOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Read/Write">
																				<ToggleButton value="2">
																					<EditOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																		</ToggleButtonGroup>
																	</TableCell>
																	<TableCell>
																		<ToggleButtonGroup
																			value={values.communicationProfiles}
																			exclusive
																			onChange={(e, value) => {
																				setFieldValue(
																					'communicationProfiles',
																					value,
																				);
																			}}
																		>
																			<Tooltip title="No Access">
																				<ToggleButton value="0">
																					<VisibilityOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Read Only">
																				<ToggleButton value="1">
																					<EditOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Read/Write">
																				<ToggleButton value="2">
																					<EditOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																		</ToggleButtonGroup>
																	</TableCell>
																	<TableCell>
																		<ToggleButtonGroup
																			value={values.digitalChannels}
																			exclusive
																			onChange={(e, value) => {
																				setFieldValue('digitalChannels', value);
																			}}
																		>
																			<Tooltip title="No Access">
																				<ToggleButton value="0">
																					<VisibilityOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Read Only">
																				<ToggleButton value="1">
																					<EditOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Read/Write">
																				<ToggleButton value="2">
																					<EditOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																		</ToggleButtonGroup>
																	</TableCell>
																</TableRow>
															</TableBody>
														</Table>
														<Table>
															<TableHead>
																<TableRow>
																	<TableCell>Suppression</TableCell>
																	<TableCell>Live Agent Select</TableCell>
																	<TableCell>Connection</TableCell>
																	<TableCell>User Management</TableCell>
																</TableRow>
															</TableHead>
															<TableBody>
																<TableRow>
																	<TableCell>
																		<ToggleButtonGroup
																			value={values.suppression}
																			exclusive
																			onChange={(e, value) => {
																				setFieldValue('suppression', value);
																			}}
																		>
																			<Tooltip title="No Access">
																				<ToggleButton value="0">
																					<VisibilityOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Read Only">
																				<ToggleButton value="1">
																					<EditOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Read/Write">
																				<ToggleButton value="2">
																					<EditOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																		</ToggleButtonGroup>
																	</TableCell>
																	<TableCell>
																		<ToggleButtonGroup
																			value={values.liveAgentSelect}
																			exclusive
																			onChange={(e, value) => {
																				setFieldValue('liveAgentSelect', value);
																				if (value > 0) {
																					setFieldValue('approver', '1');
																				} else {
																					setFieldValue('approver', '0');
																				}
																			}}
																		>
																			<Tooltip title="No Access">
																				<ToggleButton value="0">
																					<VisibilityOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Approver">
																				<ToggleButton value="2">
																					<AdsClickOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																		</ToggleButtonGroup>
																	</TableCell>
																	<TableCell>
																		<ToggleButtonGroup
																			value={values.connection}
																			exclusive
																			onChange={(e, value) => {
																				setFieldValue('connection', value);
																			}}
																		>
																			<Tooltip title="No Access">
																				<ToggleButton value="0">
																					<VisibilityOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Read Only">
																				<ToggleButton value="1">
																					<EditOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Read/Write">
																				<ToggleButton value="2">
																					<EditOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																		</ToggleButtonGroup>
																	</TableCell>
																	<TableCell>
																		<ToggleButtonGroup
																			value={values.userManagement}
																			exclusive
																			onChange={(e, value) => {
																				setFieldValue('userManagement', value);
																			}}
																		>
																			<Tooltip title="No Access">
																				<ToggleButton value="0">
																					<VisibilityOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Read Only">
																				<ToggleButton value="1">
																					<EditOffOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																			<Tooltip title="Read/Write">
																				<ToggleButton value="2">
																					<EditOutlined color="primary" />
																				</ToggleButton>
																			</Tooltip>
																		</ToggleButtonGroup>
																	</TableCell>
																</TableRow>
															</TableBody>
														</Table>
													</CardContent>
												</Card>
											</Box>
											<DialogActions>
												<Button
													sx={actionTwoButtonStyle}
													onClick={() => {
														if (newUser) {
															setUserSettings(false);
															setNewUser(false);
															setEditUserSettings(false);
														} else {
															setEditUserSettings(false);
														}
													}}
												>
													Cancel
												</Button>

												<Button sx={actionOneButtonStyle} type="submit">
													Save
												</Button>
											</DialogActions>
										</form>
									);
								}}
							</Formik>
						)}

						{!editUserSettings && (
							<Box
								display="flex"
								flexDirection="column"
								gap={2}
								sx={{ marginTop: 2 }}
							>
								<Box display="flex" gap={2}>
									<Box display="flex" flexDirection="column">
										<Typography variant="overline" color="primary">
											Name
										</Typography>
										<Typography variant="h5">{editUser.name}</Typography>
									</Box>
									<Box display="flex" flexDirection="column">
										<Typography variant="overline" color="primary">
											Email
										</Typography>
										<Typography variant="h6">{editUser.email}</Typography>
									</Box>
									<Box display="flex" flexDirection="column">
										<Typography variant="overline" color="primary">
											Company
										</Typography>
										<Typography variant="h6">{editUser.company}</Typography>
									</Box>
								</Box>
								<Table>
									<TableHead>
										<TableRow>
											<TableCell align="center">Contacts</TableCell>
											<TableCell align="center">Inventory Management</TableCell>
											<TableCell align="center">Campaigns</TableCell>
											<TableCell align="center">
												Communication Profiles
											</TableCell>
											<TableCell align="center">Digital Channels</TableCell>
											<TableCell align="center">Suppression</TableCell>
											<TableCell align="center">Live Agent Select</TableCell>
											<TableCell align="center">Connection</TableCell>
											<TableCell align="center">User Management</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										<TableRow>
											<TableCell align="center">
												{editUser.contacts === '0' ? (
													<Tooltip title="No Access">
														<VisibilityOffOutlined color="primary" />
													</Tooltip>
												) : editUser.contacts === '1' ? (
													<Tooltip title="Read Only">
														<EditOffOutlined color="primary" />
													</Tooltip>
												) : (
													<Tooltip title="Read/Write">
														<EditOutlined color="primary" />
													</Tooltip>
												)}
											</TableCell>
											<TableCell align="center">
												{editUser.inventoryManagement === '0' ? (
													<Tooltip title="No Access">
														<VisibilityOffOutlined color="primary" />
													</Tooltip>
												) : editUser.inventoryManagement === '1' ? (
													<Tooltip title="Read Only">
														<EditOffOutlined color="primary" />
													</Tooltip>
												) : (
													<Tooltip title="Read/Write">
														<EditOutlined color="primary" />
													</Tooltip>
												)}
											</TableCell>
											<TableCell align="center">
												{editUser.campaigns === '0' ? (
													<Tooltip title="No Access">
														<VisibilityOffOutlined color="primary" />
													</Tooltip>
												) : editUser.campaigns === '1' ? (
													<Tooltip title="Read Only">
														<EditOffOutlined color="primary" />
													</Tooltip>
												) : (
													<Tooltip title="Read/Write">
														<EditOutlined color="primary" />
													</Tooltip>
												)}
											</TableCell>
											<TableCell align="center">
												{editUser.communicationProfiles === '0' ? (
													<Tooltip title="No Access">
														<VisibilityOffOutlined color="primary" />
													</Tooltip>
												) : editUser.communicationProfiles === '1' ? (
													<Tooltip title="Read Only">
														<EditOffOutlined color="primary" />
													</Tooltip>
												) : (
													<Tooltip title="Read/Write">
														<EditOutlined color="primary" />
													</Tooltip>
												)}
											</TableCell>
											<TableCell align="center">
												{editUser.digitalChannels === '0' ? (
													<Tooltip title="No Access">
														<VisibilityOffOutlined color="primary" />
													</Tooltip>
												) : editUser.digitalChannels === '1' ? (
													<Tooltip title="Read Only">
														<EditOffOutlined color="primary" />
													</Tooltip>
												) : (
													<Tooltip title="Read/Write">
														<EditOutlined color="primary" />
													</Tooltip>
												)}
											</TableCell>
											<TableCell align="center">
												{editUser.suppression === '0' ? (
													<Tooltip title="No Access">
														<VisibilityOffOutlined color="primary" />
													</Tooltip>
												) : editUser.suppression === '1' ? (
													<Tooltip title="Read Only">
														<EditOffOutlined color="primary" />
													</Tooltip>
												) : (
													<Tooltip title="Read/Write">
														<EditOutlined color="primary" />
													</Tooltip>
												)}
											</TableCell>
											<TableCell align="center">
												{editUser.liveAgentSelect > 0 &&
												editUser.approver === '1' ? (
													<Tooltip title="Approver">
														<AdsClickOutlined color="primary" />
													</Tooltip>
												) : (
													<Tooltip title="No Access">
														<VisibilityOffOutlined color="primary" />
													</Tooltip>
												)}
											</TableCell>
											<TableCell align="center">
												{editUser.connection === '0' ? (
													<Tooltip title="No Access">
														<VisibilityOffOutlined color="primary" />
													</Tooltip>
												) : editUser.connection === '1' ? (
													<Tooltip title="Read Only">
														<EditOffOutlined color="primary" />
													</Tooltip>
												) : (
													<Tooltip title="Read/Write">
														<EditOutlined color="primary" />
													</Tooltip>
												)}
											</TableCell>
											<TableCell align="center">
												{editUser.userManagement === '0' ? (
													<Tooltip title="No Access">
														<VisibilityOffOutlined color="primary" />
													</Tooltip>
												) : editUser.userManagement === '1' ? (
													<Tooltip title="Read Only">
														<EditOffOutlined color="primary" />
													</Tooltip>
												) : (
													<Tooltip title="Read/Write">
														<EditOutlined color="primary" />
													</Tooltip>
												)}
											</TableCell>
										</TableRow>
									</TableBody>
								</Table>
								<DialogActions>
									<Button
										sx={actionTwoButtonStyle}
										onClick={() => {
											setUserSettings(false);
											setEditUserSettings(false);
											setEditUser({});
											setNewUser(false);
										}}
									>
										Close
									</Button>

									{userContext.securityOptions.userManagement > 1 && (
										<Button
											sx={actionOneButtonStyle}
											onClick={() => {
												setEditUserSettings(true);
											}}
										>
											Edit
										</Button>
									)}
								</DialogActions>
							</Box>
						)}
					</DialogContent>
				</Dialog>
			</Grid>
		</>
	);
}
